import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, ArrowLeft, Loader2, Sparkles, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

/**
 * Customer-facing "Start New Order" page. Captures the customer's project
 * brief + optional logo/mockup upload and drops a `customer_quote_requests`
 * row in Mongo for admin to convert into a formal proposal. The interactive
 * mockup designer link is shown as a follow-on once the request is in.
 */
const CustomerNewOrder = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [deadline, setDeadline] = useState('');
  const [notes, setNotes] = useState('');
  const [designPreview, setDesignPreview] = useState(null);
  const [designB64, setDesignB64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setDesignPreview(reader.result);
      setDesignB64(reader.result.split(',')[1] || null);
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return toast.error('Tell us what you need');
    setLoading(true);
    try {
      const token = localStorage.getItem('customerToken');
      const { data } = await axios.post(
        '/customer/quote-request',
        {
          description: description.trim(),
          quantity: quantity ? Number(quantity) : null,
          deadline: deadline || null,
          notes: notes.trim() || null,
          design_base64: designB64 || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmittedId(data.id);
      toast.success("Got it! Our team will review and reach out shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submittedId) {
    return (
      <div style={pageBg}>
        <div style={card} data-testid="quote-request-success">
          <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 12 }}>✅</div>
          <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>Request received</h2>
          <p style={{ color: '#64748b', marginBottom: 20 }}>
            We&apos;ll email you a custom proposal within 1 business day. You can
            track this and all your other orders right from the portal.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button onClick={() => navigate('/portal')} data-testid="back-to-portal">Back to Portal</Button>
            <Button variant="outline" onClick={() => { setSubmittedId(null); setDescription(''); setQuantity(''); setDeadline(''); setNotes(''); setDesignPreview(null); setDesignB64(null); }}>
              Submit another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageBg}>
      <div style={card} data-testid="customer-new-order">
        <button onClick={() => navigate('/portal')} style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', cursor: 'pointer', marginBottom: 16 }}>
          <ArrowLeft size={16} /> Back to portal
        </button>
        <h1 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={22} color="#e74c3c" /> Start a New Order
        </h1>
        <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14 }}>
          Tell us about your project — we&apos;ll build a custom proposal with mockups for your approval.
        </p>

        <form onSubmit={submit}>
          <Field label="What do you need printed? *">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. 50 navy unisex tees with our gym logo on the front, white ink, screen print."
              rows={4}
              data-testid="qr-description"
              style={textarea}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Quantity">
              <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="50" data-testid="qr-quantity" min="1" />
            </Field>
            <Field label="Need-by date">
              <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="qr-deadline" />
            </Field>
          </div>

          <Field label="Upload your logo / artwork (optional)">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.svg,.pdf"
              onChange={handleFile}
              style={{ display: 'none' }}
              data-testid="qr-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="qr-upload-btn"
              style={uploadBtn}
            >
              {designPreview ? (
                <img src={designPreview} alt="preview" style={{ maxHeight: 90, maxWidth: '100%', borderRadius: 6 }} />
              ) : (
                <>
                  <Upload size={20} />
                  <span style={{ fontWeight: 600 }}>Click to upload</span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>PNG, JPG, SVG, or PDF · max 5MB</span>
                </>
              )}
            </button>
          </Field>

          <Field label="Anything else we should know?">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Sizes breakdown, brand preferences, color codes, etc."
              rows={3}
              data-testid="qr-notes"
              style={textarea}
            />
          </Field>

          <Button type="submit" disabled={loading} data-testid="qr-submit" style={submitBtn}>
            {loading ? <Loader2 size={18} className="animate-spin" /> : (<><Send size={16} style={{ marginRight: 6 }} /> Send Request</>)}
          </Button>

          <div style={{ marginTop: 14, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            We typically reply within 1 business day with a mockup + price.
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>
    {children}
  </div>
);

const pageBg = { minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px' };
const card = { width: '100%', maxWidth: 640, background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' };
const textarea = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' };
const uploadBtn = { width: '100%', padding: 16, background: '#f8fafc', border: '1px dashed #94a3b8', borderRadius: 8, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#475569' };
const submitBtn = { width: '100%', height: 48, background: 'linear-gradient(135deg,#e74c3c,#c0392b)', color: '#fff', fontSize: 15, fontWeight: 700, marginTop: 6 };

export default CustomerNewOrder;
